/**
 * Frontend-typer for LabelDesigner (canvas-editor).
 * Skal matche BizClick.WMS.Shared.DTOs.Reporting.LabelDesignDTOs.
 */

export type LabelElementType =
  | 'Text'
  | 'Barcode'
  | 'QrCode'
  | 'Image'
  | 'Rectangle'
  | 'Line';

export type BarcodeFormat = 'Code128' | 'Code39' | 'Ean13' | 'Ean8' | 'Gs1128';

export type TextAlign = 'Left' | 'Center' | 'Right';

/**
 * Et enkelt element på etiketten. Posisjoner/dimensjoner i millimeter
 * relativt til øvre venstre hjørne av etiketten.
 */
export interface LabelElement {
  id: string;
  type: LabelElementType;

  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;

  // Text
  text?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  fontFamily?: string;
  textAlign?: TextAlign;
  color?: string;

  // Barcode / QR
  value?: string;
  barcodeFormat?: BarcodeFormat;
  showText?: boolean;

  // Image
  imageSource?: string;

  // Shape
  fillColor?: string;
  borderColor?: string;
  borderWidth?: number;
}

export interface LabelLayout {
  version: number;
  widthMm: number;
  heightMm: number;
  backgroundColor?: string;
  /**
   * Hvis true, sender backend `fit_to_page=true` til PrintNode slik at skriveren
   * skalerer PDF-en til faktisk media-størrelse. Anbefales kun for ren tekst —
   * strekkoder kan bli uskannbare ved skalering. Default false.
   */
  fitToPage?: boolean;
  elements: LabelElement[];
}

export interface CustomLabelPreviewRequest {
  layout: LabelLayout;
  sampleData?: Record<string, string | null | undefined>;
}

/**
 * Autoritativ plassholder-modell — matcher backend LabelPlaceholder DTO.
 * Hentes dynamisk via labelPlaceholderService.
 */
export interface LabelPlaceholder {
  key: string;
  label: string;
  exampleValue: string;
  category: string;
  applicableReportTypes: number[];
}

/**
 * En kolonne i en tabell — kan settes inn som plassholder `{{Table.Column}}`.
 * Matcher backend TableColumnDTO.
 */
export interface TableColumn {
  name: string;
  sqlType: string;
  nullable: boolean;
  maxLength?: number | null;
}

/**
 * Skjema for én tabell eksponert i plassholder-picker.
 * Matcher backend TableSchemaDTO.
 */
export interface TableSchema {
  tableName: string;
  displayName: string;
  columns: TableColumn[];
}

/**
 * Kontekst for DB-oppslag i etikett-preview / -utskrift.
 * Alle felter er valgfrie — kun satt felt trigger oppslag i tilhørende tabell.
 * Matcher backend LabelContext.
 */
export interface LabelContext {
  itemNo?: string;
  orderNo?: number;
  orderLineNo?: number;
  pOrderNo?: number;
  pOrderLineNo?: number;
  warehouseID?: number;
  binCode?: string;
  /** PList.ID — trigger oppslag i PListSSCC (gir SSCC, PalletNo). */
  pListID?: number;
}

/**
 * Fallback-liste brukt hvis API-kallet feiler (offline/utvikling uten API).
 * Den faktiske listen hentes fra backend — se labelPlaceholderService.
 */
export const FALLBACK_PLACEHOLDERS: LabelPlaceholder[] = [
  { key: 'ItemNo', label: 'Varenummer', exampleValue: 'ART-12345', category: 'Vare', applicableReportTypes: [] },
  { key: 'ItemName', label: 'Varenavn', exampleValue: 'Skrue M6 x 30mm', category: 'Vare', applicableReportTypes: [] },
  { key: 'BinCode', label: 'Lokasjon', exampleValue: 'A-01-02-03', category: 'Lager', applicableReportTypes: [] },
  { key: 'BatchNo', label: 'Batchnummer', exampleValue: 'LOT-2026-A', category: 'Batch/serie', applicableReportTypes: [] },
  { key: 'SerialNo', label: 'Serienummer', exampleValue: 'SN-9876', category: 'Batch/serie', applicableReportTypes: [] },
  { key: 'Quantity', label: 'Antall', exampleValue: '100', category: 'Batch/serie', applicableReportTypes: [] },
  { key: 'CompanyName', label: 'Firmanavn', exampleValue: 'Demo Lager AS', category: 'Firma', applicableReportTypes: [] }
];

export function createEmptyLayout(widthMm: number = 100, heightMm: number = 50): LabelLayout {
  return {
    version: 1,
    widthMm,
    heightMm,
    elements: []
  };
}

export function createElement(
  type: LabelElementType,
  x: number,
  y: number
): LabelElement {
  const id = `el-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const base: LabelElement = { id, type, x, y, width: 30, height: 10 };

  switch (type) {
    case 'Text':
      return { ...base, text: 'Ny tekst', fontSize: 10, textAlign: 'Left' };
    case 'Barcode':
      return { ...base, value: '{{ItemNo}}', barcodeFormat: 'Code128', showText: true, height: 15 };
    case 'QrCode':
      return { ...base, value: '{{ItemNo}}', width: 20, height: 20 };
    case 'Image':
      return { ...base, imageSource: 'logo', width: 25, height: 15 };
    case 'Rectangle':
      return { ...base, borderColor: '#000000', borderWidth: 0.3 };
    case 'Line':
      return { ...base, borderColor: '#000000', borderWidth: 0.3, height: 0.3 };
  }
}
